import { useEffect, useRef, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { apiClient } from '@shared/api';
import { cn } from '@/lib/utils';

/**
 * Renders one page image of a scan (86d3rrfdk), fetched as an authed blob.
 *
 * The image endpoints require the bearer token, so a plain `<img src>` can't be used (it wouldn't
 * carry the header). We fetch through apiClient, turn the blob into an object URL, and revoke it on
 * unmount so we don't leak. One component serves both the review side-by-side and the viewer canvas.
 *
 * Pass exactly one of `docNumber` (attached docs) or `reviewItemId` (unattached scans — the only way
 * to reach a file with no document number).
 */
interface ScanImageProps {
  docNumber?: string;
  reviewItemId?: number;
  page: number;
  version?: number;
  alt: string;
  className?: string;
}

export function ScanImage({ docNumber, reviewItemId, page, version, alt, className }: ScanImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  // Track the object URL across renders so the cleanup always revokes the right one.
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    const path =
      reviewItemId != null
        ? `/document-capture/review/${reviewItemId}/pages/${page}`
        : `/document-capture/documents/${encodeURIComponent(docNumber ?? '')}/pages/${page}${
            version != null ? `?v=${version}` : ''
          }`;

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(res.data);
        urlRef.current = objectUrl;
        setUrl(objectUrl);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [docNumber, reviewItemId, page, version]);

  if (state === 'loading') {
    return (
      <div className={cn('flex items-center justify-center bg-muted', className)}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <span className="sr-only">Loading scan…</span>
      </div>
    );
  }

  if (state === 'error' || !url) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground', className)}>
        <ImageOff className="h-6 w-6" aria-hidden />
        <span className="text-xs">Scan image unavailable</span>
      </div>
    );
  }

  return <img src={url} alt={alt} className={cn('object-contain', className)} />;
}
